/**
 * Microsoft Authentication Library (MSAL) configuration.
 *
 * Configure Azure AD authentication for the frontend application.
 * Users sign in with their Microsoft accounts.
 */

import {
  Configuration,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";

export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || "",
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || "common"}`,
    redirectUri:
      import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage", // Use sessionStorage for better security
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;

        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
        }
      },
      logLevel:
        import.meta.env.VITE_ENV === "development"
          ? LogLevel.Warning
          : LogLevel.Error,
    },
  },
};

/**
 * Scopes for the access token.
 *
 * The backend API scope should be:
 * api://<backend-client-id>/access_as_user
 */
export const loginRequest = {
  scopes: ["openid", "profile", "email"],
};

/**
 * Scopes for API calls.
 *
 * Targets the backend's custom Application ID URI scope so Azure mints
 * an access token whose `aud` matches the backend's exposed API rather
 * than MS Graph.
 *
 * Host is reused from VITE_AZURE_REDIRECT_URI (protocol stripped) — the
 * Application ID URI in Azure AD is always set to the same host the
 * frontend is served from, so there's no need for a separate env var.
 *
 * Resulting scope: api://<host>/<VITE_AZURE_CLIENT_ID>/access_as_user
 */
const REDIRECT_URI = import.meta.env.VITE_AZURE_REDIRECT_URI;
const CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID;
if (!REDIRECT_URI || !CLIENT_ID) {
  throw new Error(
    "VITE_AZURE_REDIRECT_URI and VITE_AZURE_CLIENT_ID must both be set " +
      "in frontend/.env (see frontend/.env.copy).",
  );
}
const AUDIENCE_HOST = REDIRECT_URI.replace(/^https?:\/\//, "").split("/")[0];
const BACKEND_API_SCOPE = `api://${AUDIENCE_HOST}/${CLIENT_ID}/access_as_user`;

export const apiRequest = {
  scopes: [BACKEND_API_SCOPE],
};

/**
 * Create and export the MSAL instance.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
msalInstance.initialize().catch(console.error);
